<script setup>
import SiteSwitcher from '#modules/sites/components/SiteSwitcher.vue';
import CFSidebarLink from '@/shared/components/CFSidebarLink.vue';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import CFRoundedButton from '@/ui/RoundedButton/CFRoundedButton.vue';
import { useSite } from '#modules/sites/queries/useSite.js';
import CFSidebar from '@/shared/components/CFSidebar.vue';
import { SiteStatus } from '#modules/sites/models/SiteStatus.js';
import { SiteLanguage } from '#modules/sites/models/SiteLanguage.js';

const { siteId, site, isPending, isError } = useSite();

const { t } = useI18n();

const sidebarLinks = [
    {
        title: t('sites.sidebar.overview'),
        icon: 'layout-dashboard',
        href: computed(() => ({
            name: 'sites.show',
            params: { site: siteId.value },
        })),
    },
    {
        title: t('sites.sidebar.sftp'),
        icon: 'sftp',
        href: computed(() => ({
            name: 'sites.sftp',
            params: { site: siteId.value },
        })),
    },
    {
        title: t('sites.sidebar.databases'),
        icon: 'database',
        href: computed(() => ({
            name: 'sites.databases.index',
            params: { site: siteId.value },
        })),
        exact: false,
        requires: [SiteLanguage.PHP],
    },
    {
        title: t('sites.sidebar.ssh_keys'),
        icon: 'login-key',
        href: computed(() => ({
            name: 'sites.ssh-keys',
            params: { site: siteId.value },
        })),
        exact: false,
    },
    {
        title: t('sites.sidebar.redis_instances'),
        icon: 'database-key',
        href: computed(() => ({
            name: 'sites.redis',
            params: { site: siteId.value },
        })),
        requires: [SiteLanguage.PHP],
    },
    {
        title: t('sites.sidebar.ssl_certificates'),
        icon: 'lock',
        href: computed(() => ({
            name: 'sites.ssl',
            params: { site: siteId.value },
        })),
    },
    {
        title: t('sites.sidebar.password_protection'),
        icon: 'folder-lock',
        href: computed(() => ({
            name: 'sites.basic-authentication',
            params: { site: siteId.value },
        })),
    },
    {
        title: t('sites.sidebar.access_logs'),
        icon: 'notes-clock',
        href: computed(() => ({
            name: 'sites.access-logs',
            params: { site: siteId.value },
        })),
    },
    {
        title: t('sites.sidebar.error_logs'),
        icon: 'notes-warning',
        href: computed(() => ({
            name: 'sites.error-logs',
            params: { site: siteId.value },
        })),
    },
    {
        title: t('sites.sidebar.backups'),
        icon: 'server-clock',
        href: computed(() => ({
            name: 'sites.backups',
            params: { site: siteId.value },
        })),
    },
    {
        title: t('sites.sidebar.cron_jobs'),
        icon: 'calendar-refresh',
        href: computed(() => ({
            name: 'sites.cron-jobs',
            params: { site: siteId.value },
        })),
        requires: [SiteLanguage.PHP, SiteLanguage.NodeJS],
    },
    {
        title: t('sites.sidebar.malware'),
        icon: 'computer-bug1',
        href: computed(() => ({
            name: 'sites.malware',
            params: { site: siteId.value },
        })),
    },
    {
        title: t('sites.sidebar.file_manager'),
        icon: 'hierarchy-files',
        href: computed(() => ({
            name: 'sites.file-manager',
            params: { site: siteId.value },
        })),
    },
    // {
    //     title: t('sites.sidebar.monitoring'),
    //     icon: 'app-window-graph',
    //     href: computed(() => ({
    //         name: 'sites.monitoring',
    //         params: { site: siteId.value },
    //     })),
    // },
];

const activeSidebarLinks = computed(() => {
    return sidebarLinks.filter((link) => {
        if (link.requires) {
            return link.requires.includes(site.value.attributes.language);
        }

        return true;
    });
});
</script>

<template>
    <CFSidebar>
        <div class="relative px-6 pt-9 pb-10">
            <RouterLink
                :to="{ name: 'sites.index' }"
                class="mb-4 flex items-center"
            >
                <CFRoundedButton
                    icon="arrow-left"
                    color="secondary"
                    class="me-2"
                />
                {{ $t('sites.back_to_sites') }}
            </RouterLink>

            <template v-if="!isPending && !isError && site">
                <SiteSwitcher />

                <template v-if="site?.attributes?.status === SiteStatus.Submitted">
                    <CFSidebarLink
                        v-for="link in activeSidebarLinks"
                        :key="link.title"
                        :title="link.title"
                        :href="link.href"
                        :icon="link.icon"
                        class="py-1.5"
                        :exact="link.exact !== false"
                    />
                </template>
            </template>
        </div>
    </CFSidebar>
</template>
